import dayjs from 'dayjs';

/**
 * Zeitlogik fuer geplante Schichten (Collection `plannedShifts`).
 *
 * Eine geplante Schicht ist eine Serie: dieselbe Startzeit und Dauer an
 * mehreren Starttagen (`dates`). Alle Pruefungen rechnen mit absoluten
 * Zeitpunkten je Starttag — nur so werden Nachtschichten, die in den
 * Folgetag hineinlaufen, korrekt gegen andere Schichten geprueft.
 * Doku: nextcab_mobile/docs/general/schichtplaner_ueberarbeitung.md
 */

const toDuration = (plannedDuration) => {
  const duration = Number(plannedDuration);
  return Number.isFinite(duration) && duration > 0 ? duration : 8;
};

// startTime ist in Firestore ein 'HH:mm'-String.
const parseStartTime = (startTime) => {
  const [hours, minutes] = String(startTime || '00:00').split(':').map(Number);
  return { hours: hours || 0, minutes: minutes || 0 };
};

export const getShiftInterval = (date, startTime, plannedDuration) => {
  const { hours, minutes } = parseStartTime(startTime);
  const start = dayjs(date).hour(hours).minute(minutes).second(0).millisecond(0);
  return { date, start, end: start.add(toDuration(plannedDuration), 'hour') };
};

export const getPlannedShiftIntervals = (shift) => {
  if (!shift || !Array.isArray(shift.dates)) return [];
  return shift.dates.map((date) => getShiftInterval(date, shift.startTime, shift.plannedDuration));
};

export const intervalsOverlap = (a, b) => a.start.isBefore(b.end) && b.start.isBefore(a.end);

// Kalendertag, an dem die Schicht endet. Endet sie genau um 00:00, zaehlt sie
// nicht als Nachtschicht (sonst entstuende am Folgetag ein 0-Stunden-Stueck).
export const getIntervalEndDate = (interval) => interval.end.subtract(1, 'millisecond').format('YYYY-MM-DD');

/**
 * Abgeleitete Felder einer Schichtserie. Werden bei jedem Anlegen, Bearbeiten
 * und Teil-Stornieren neu berechnet, damit `dateEndMapping` nie Starttage
 * enthaelt, die es nicht mehr gibt.
 */
export const buildScheduleFields = ({ dates, startTime, plannedDuration }) => {
  const sortedDates = [...new Set(dates || [])].sort();
  const intervals = sortedDates.map((date) => getShiftInterval(date, startTime, plannedDuration));
  const dateEndMapping = {};
  let isOvernightShift = false;

  intervals.forEach((interval) => {
    const endDate = getIntervalEndDate(interval);
    if (endDate !== interval.date) {
      isOvernightShift = true;
      dateEndMapping[interval.date] = endDate;
    }
  });

  const first = intervals[0];
  return {
    dates: sortedDates,
    isOvernightShift,
    dateEndMapping,
    // Einzelwerte aus der Anfangszeit (Kompatibilitaet mit Altdaten/Lesern)
    endDate: first ? getIntervalEndDate(first) : null,
    startDateTime: first ? first.start.toISOString() : null,
    endDateTime: first ? first.end.toISOString() : null
  };
};

/**
 * Alle Ueberschneidungen einer (neuen/geaenderten) Schichtserie mit
 * bestehenden Serien. `existingShifts` sollte schon auf Fahrer bzw. Fahrzeug
 * gefiltert sein; stornierte Serien werden hier ignoriert.
 */
export const findPlannedShiftConflicts = (candidate, existingShifts, { ignoreId = null } = {}) => {
  const candidateIntervals = getPlannedShiftIntervals(candidate);
  const conflicts = [];

  (existingShifts || []).forEach((existing) => {
    if (!existing || existing.status === 'cancelled') return;
    if (ignoreId && existing.id === ignoreId) return;
    getPlannedShiftIntervals(existing).forEach((existingInterval) => {
      candidateIntervals.forEach((candidateInterval) => {
        if (intervalsOverlap(candidateInterval, existingInterval)) {
          conflicts.push({
            date: candidateInterval.date,
            existingShiftId: existing.id,
            existingDate: existingInterval.date,
            existingStartTime: existing.startTime,
            existingDuration: existing.plannedDuration
          });
        }
      });
    });
  });

  return conflicts;
};

export const formatConflictMessage = (subject, conflicts) => {
  const days = [...new Set(conflicts.map((c) => dayjs(c.date).format('DD.MM.YYYY')))];
  const first = conflicts[0];
  const detail = first
    ? ` (bestehende Schicht ${dayjs(first.existingDate).format('DD.MM.')} ab ${first.existingStartTime}, ${first.existingDuration} h)`
    : '';
  return `${subject} ist bereits eingeplant: ${days.join(', ')}${detail}`;
};

/**
 * Tatsaechliche App-Schichten (Collection `shifts/{YYYY-MM-DD}`) eines
 * Fahrers, die zu einem geplanten Zeitfenster passen. Beginn bis 4 h vor dem
 * Plan-Start zaehlt noch (Fahrer faengt frueher an).
 */
const EARLY_START_TOLERANCE_HOURS = 4;

export const getActualShiftStart = (actualShift) => {
  if (actualShift?.shiftStart?.seconds) return dayjs.unix(actualShift.shiftStart.seconds);
  if (actualShift?.startTime) return dayjs(actualShift.startTime);
  return null;
};

export const getActualShiftEnd = (actualShift) => {
  if (actualShift?.shiftEnd?.seconds) return dayjs.unix(actualShift.shiftEnd.seconds);
  if (actualShift?.endTime) return dayjs(actualShift.endTime);
  return null;
};

export const matchActualShifts = (driverShiftEntries, plannedStart, plannedEnd) => {
  const windowStart = plannedStart.subtract(EARLY_START_TOLERANCE_HOURS, 'hour');
  const seen = new Set();
  const matches = [];

  (driverShiftEntries || []).forEach((driverData) => {
    (driverData?.shifts || []).forEach((actualShift) => {
      const start = getActualShiftStart(actualShift);
      if (!start) return;
      if (start.isBefore(windowStart) || !start.isBefore(plannedEnd)) return;
      // Dieselbe Schicht kann ueber Vortag + Tag doppelt geladen sein
      const key = start.valueOf();
      if (seen.has(key)) return;
      seen.add(key);
      matches.push(actualShift);
    });
  });

  return matches.sort((a, b) => getActualShiftStart(a).valueOf() - getActualShiftStart(b).valueOf());
};
